import { useEffect, useRef, useState } from "react";
import {
  Check,
  Loader2,
  Cloud,
  Hammer,
  Upload,
  Globe,
  AlertCircle,
  ChevronDown,
  ExternalLink,
  Wrench,
  StopCircle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const GRADIENT =
  "linear-gradient(135deg, rgba(134,87,241,0.18) 0%, rgba(107,63,214,0.08) 100%)";

interface DeployStep {
  name: string;
  status: "pending" | "active" | "done" | "error";
}

interface DeployProgressData {
  deployment_id: string;
  app_name: string;
  provider: string;
  status: "creating" | "building" | "deploying" | "active" | "error" | "auto_fixing";
  steps: DeployStep[];
  live_url?: string | null;
  error_message?: string | null;
  // Legacy single-blob log field — populated by the autofix path's
  // "last 30 lines" dump on terminal failure. Kept for back-compat
  // with deploys that don't stream live (DO path, pre-Session 2.4a).
  logs?: string | null;
  // Session 2.4a — live build-log stream from GitHub Actions, polled
  // and broadcast incrementally during CF deploys. Cumulative array;
  // each progress widget update carries the full current snapshot
  // (capped server-side at 5,000 lines).
  live_logs?: string[] | null;
  // Count of lines dropped from the front of `live_logs` once the
  // 5k cap was hit. Rendered as a "[N earlier lines truncated]"
  // banner above the terminal so users know the buffer rolled.
  live_logs_truncated?: number | null;
  // Subdomain claimed during the deploy widget flow, before the app
  // itself goes live. Shown as an "Assigned domain" hint so the user
  // knows where it'll land once the build finishes.
  claimed_subdomain?: string | null;
  claimed_full_domain?: string | null;
  // Autonomous fix loop fields
  substatus?: string | null;
  attempt?: number | null;
  max_attempts?: number | null;
  needs_user_action?: boolean;
  user_action_message?: string | null;
  // ISO timestamp stamped when the deploy entered a terminal state.
  // Drives the relative-time label on the compact summary card.
  completed_at?: string | null;
  // What kicked the deploy off — "manual", "redeploy", or "push".
  // Surfaced on the compact card as a small "via push" / "via redeploy"
  // tag so users can tell at a glance how a given deploy was triggered.
  trigger?: "manual" | "redeploy" | "push" | null;
}

interface DeployProgressWidgetProps {
  data: DeployProgressData;
  onAction?: (actionId: string, context?: Record<string, unknown>) => void;
}

const STEP_ICONS = [Cloud, Hammer, Upload, Globe];
const AUTOFIX_STEP_ICONS = [Cloud, Hammer, Wrench, Globe];

function StepIcon({ step, icon: Icon }: { step: DeployStep; icon: typeof Cloud }) {
  if (step.status === "done") {
    return (
      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-daydra-success/15">
        <Check size={12} className="text-daydra-success" />
      </div>
    );
  }
  if (step.status === "active") {
    return (
      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-daydra-accent/15">
        <Loader2 size={12} className="text-daydra-accent animate-spin" />
      </div>
    );
  }
  if (step.status === "error") {
    return (
      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-500/15">
        <AlertCircle size={12} className="text-red-400" />
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/[0.04]">
      <Icon size={12} className="text-daydra-muted/40" />
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  if (diffMs < 0) return "just now";
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  // For older deploys, fall back to a short absolute date.
  return new Date(then).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function formatTrigger(trigger: string | null | undefined): string | null {
  switch (trigger) {
    case "push":
      return "via push";
    case "redeploy":
      return "via redeploy";
    case "manual":
      return null; // manual is the default — no badge needed
    default:
      return null;
  }
}

// ── Live build-log terminal ──────────────────────────────────────
//
// Scrollable mono block fed by `live_logs` (Session 2.4a). Auto-
// scrolls to the bottom as new lines arrive UNLESS the user has
// scrolled up to read past output — that scroll-up gesture is
// treated as a "I'm reading, don't yank me away" hint and we hold
// the position until they scroll back to the bottom.

interface BuildLogTerminalProps {
  lines: string[];
  truncated?: number | null;
  // When true, shows the empty placeholder instead of an empty
  // <pre> while we wait for the first lines to arrive. Avoids the
  // jarring "card has logs section but it's blank" frame.
  showWhileEmpty?: boolean;
}

function BuildLogTerminal({ lines, truncated, showWhileEmpty }: BuildLogTerminalProps) {
  const scrollRef = useRef<HTMLPreElement | null>(null);
  // Track whether we should auto-scroll. Flipped to false when the
  // user scrolls up; flipped back to true when they scroll to the
  // bottom (within a small slop margin).
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [lines.length]);

  function handleScroll(e: React.UIEvent<HTMLPreElement>) {
    const el = e.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 24;
  }

  if (lines.length === 0 && !showWhileEmpty) return null;

  return (
    <div className="mb-3">
      {(truncated ?? 0) > 0 && (
        <div className="text-2xs text-daydra-muted/70 mb-1 px-1">
          [{truncated} earlier line{truncated === 1 ? "" : "s"} truncated]
        </div>
      )}
      <pre
        ref={scrollRef}
        onScroll={handleScroll}
        className="p-2 rounded-lg bg-black/40 text-2xs text-daydra-muted/90 font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre"
      >
        {lines.length === 0 ? (
          <span className="text-daydra-muted/50">Waiting for build logs…</span>
        ) : (
          lines.join("\n")
        )}
      </pre>
    </div>
  );
}

// ── Compact terminal-state card ──────────────────────────────────
//
// Once a deploy lands (success or failure) the full progress card
// collapses into this one-line summary. It's the durable history
// entry — always present after refresh — and never resurrects the
// build pipeline UI, which is only useful while a build is actually
// in flight.

function CompactSuccessCard({ data }: { data: DeployProgressData }) {
  const url = data.live_url || null;
  const displayHost = url ? url.replace(/^https?:\/\//, "") : null;
  const triggerLabel = formatTrigger(data.trigger);
  const timeLabel = formatRelativeTime(data.completed_at);

  const inner = (
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-daydra-success/15 shrink-0">
        <CheckCircle2 size={14} className="text-daydra-success" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5 min-w-0">
          <span className="text-sm font-medium text-daydra-text truncate">
            Deployed {data.app_name}
          </span>
          {triggerLabel && (
            <span className="text-2xs text-daydra-muted shrink-0">
              {triggerLabel}
            </span>
          )}
        </div>
        {displayHost && (
          <div className="flex items-center gap-1 text-2xs text-daydra-muted truncate">
            <span className="truncate">{displayHost}</span>
            {timeLabel && (
              <>
                <span className="shrink-0">·</span>
                <span className="shrink-0">{timeLabel}</span>
              </>
            )}
          </div>
        )}
        {!displayHost && timeLabel && (
          <div className="text-2xs text-daydra-muted">{timeLabel}</div>
        )}
      </div>
      {url && (
        <ExternalLink
          size={12}
          className="text-daydra-muted shrink-0 group-hover:text-daydra-text transition-colors"
        />
      )}
    </div>
  );

  if (url) {
    return (
      <Card
        variant="default"
        padding="sm"
        className="max-w-sm animate-scale-in group"
      >
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          {inner}
        </a>
      </Card>
    );
  }
  return (
    <Card variant="default" padding="sm" className="max-w-sm animate-scale-in">
      {inner}
    </Card>
  );
}

function CompactFailureCard({ data }: { data: DeployProgressData }) {
  const [logsExpanded, setLogsExpanded] = useState(false);
  const triggerLabel = formatTrigger(data.trigger);
  const timeLabel = formatRelativeTime(data.completed_at);
  const reason =
    data.user_action_message ||
    data.error_message ||
    "Build failed.";
  const liveLogs = data.live_logs ?? null;
  const hasLiveLogs = !!liveLogs && liveLogs.length > 0;

  return (
    <Card variant="default" padding="sm" className="max-w-sm animate-scale-in">
      <div className="flex items-start gap-2.5 min-w-0">
        <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-red-500/15 shrink-0">
          <XCircle size={14} className="text-red-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="text-sm font-medium text-daydra-text truncate">
              Deploy failed · {data.app_name}
            </span>
            {triggerLabel && (
              <span className="text-2xs text-daydra-muted shrink-0">
                {triggerLabel}
              </span>
            )}
          </div>
          <div className="text-2xs text-daydra-muted line-clamp-2 mt-0.5">
            {reason}
          </div>
          {timeLabel && (
            <div className="text-2xs text-daydra-muted/70 mt-0.5">
              {timeLabel}
            </div>
          )}
        </div>
      </div>
      {hasLiveLogs && (
        <div className="mt-2">
          <button
            onClick={() => setLogsExpanded(!logsExpanded)}
            className="flex items-center gap-1.5 text-2xs text-daydra-muted hover:text-daydra-dim transition-colors"
          >
            <ChevronDown
              size={12}
              className={cn("transition-transform", logsExpanded && "rotate-180")}
            />
            Build logs
          </button>
          {logsExpanded && (
            <div className="mt-2">
              <BuildLogTerminal
                lines={liveLogs!}
                truncated={data.live_logs_truncated}
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export function DeployProgressWidget({ data, onAction }: DeployProgressWidgetProps) {
  const [logsExpanded, setLogsExpanded] = useState(false);
  const steps = data.steps ?? [];
  const isError = data.status === "error";
  const isAutoFixing = data.status === "auto_fixing";
  const needsUser = !!data.needs_user_action;
  const stepIcons = isAutoFixing ? AUTOFIX_STEP_ICONS : STEP_ICONS;

  // Terminal states collapse to a compact summary — that's the form
  // that lives in conversation history. The full pipeline only ever
  // renders for in-flight builds and the needs_user "stuck" state.
  if (data.status === "active") {
    return <CompactSuccessCard data={data} />;
  }
  if (isError && !needsUser) {
    return <CompactFailureCard data={data} />;
  }

  // From here on we're rendering the in-flight pipeline (or the
  // needs_user variant that asks the user to step in).
  let title = "Deploying";
  if (needsUser) title = "Needs your input";
  else if (isAutoFixing) title = "Auto-fixing build";

  return (
    <Card
      variant="default"
      padding="default"
      className="max-w-sm animate-scale-in overflow-hidden"
    >
      {/* Header with gradient background */}
      <div
        className="rounded-t-xl -mx-4 -mt-4 px-4 pt-4 pb-3 mb-3"
        style={{ background: GRADIENT }}
      >
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-daydra-accent/15">
            {isAutoFixing ? (
              <Wrench size={16} className="text-daydra-accent" />
            ) : (
              <Cloud size={16} className="text-daydra-accent" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-daydra-text">{title}</h3>
            <p className="text-2xs text-daydra-muted truncate">
              {data.app_name} · {data.provider}
              {data.attempt && data.attempt > 1 && data.max_attempts && (
                <> · attempt {data.attempt}/{data.max_attempts}</>
              )}
            </p>
            {data.substatus && (isAutoFixing || !needsUser) && (
              <p className="text-2xs text-daydra-accent/90 mt-0.5 flex items-center gap-1.5 truncate">
                <Loader2 size={10} className="animate-spin shrink-0" />
                <span className="truncate">{data.substatus}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Step pipeline */}
      <div className="space-y-0.5 mb-3">
        {steps.map((step, i) => (
          <div key={step.name} className="flex items-center gap-3 py-1.5">
            <StepIcon step={step} icon={stepIcons[i] ?? Cloud} />
            <span
              className={cn(
                "text-sm",
                step.status === "done" && "text-daydra-dim",
                step.status === "active" && "text-daydra-text font-medium",
                step.status === "error" && "text-red-400 font-medium",
                step.status === "pending" && "text-daydra-muted/50",
              )}
            >
              {step.name}
            </span>
          </div>
        ))}
      </div>

      {/* Needs-user banner — shown when autofix bailed and the user has to act */}
      {needsUser && data.user_action_message && (
        <div className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-200 text-sm mb-3">
          {data.user_action_message}
        </div>
      )}

      {/* Stop button — only while the autofix loop is still running */}
      {isAutoFixing && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-daydra-muted hover:text-red-300 mb-2"
          onClick={() =>
            onAction?.("stop_deploy", {
              __action: "stop_deploy",
              __payload: {},
            })
          }
        >
          <StopCircle size={14} className="mr-1.5" />
          Stop auto-fixing
        </Button>
      )}

      {/* Pre-claimed domain hint — shown while the build is still in
          flight so the user knows where the app will land. */}
      {data.claimed_full_domain && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-xs text-daydra-dim mb-3">
          <Globe size={12} className="text-daydra-muted shrink-0" />
          <span className="text-daydra-muted">Will be live at</span>
          <span className="font-mono text-daydra-text truncate">
            {data.claimed_full_domain}
          </span>
        </div>
      )}

      {/* Error detail — only reached when the build needs the user
          (autofix bailed). Plain failures use the compact card. */}
      {isError && needsUser && data.error_message && (
        <div className="px-3 py-2 rounded-lg bg-red-500/10 text-red-400 text-sm mb-3">
          {data.error_message}
        </div>
      )}

      {/* Live build-log terminal (Session 2.4a). Visible during
          in-flight CF deploys + the needs_user variant. Renders a
          "waiting for build logs…" placeholder while empty so the
          terminal slot doesn't pop into existence mid-build. */}
      {(data.live_logs && data.live_logs.length > 0) || (!isError && !isAutoFixing) ? (
        <BuildLogTerminal
          lines={data.live_logs ?? []}
          truncated={data.live_logs_truncated}
          showWhileEmpty
        />
      ) : null}

      {/* Legacy single-blob log fallback — autofix's "last 30 lines"
          dump on terminal failure for paths that don't stream live
          (DO + pre-2.4a CF). Suppressed when live_logs is present so
          we don't show the same content twice. */}
      {isError && needsUser && data.logs && !(data.live_logs && data.live_logs.length > 0) && (
        <div className="mb-3">
          <button
            onClick={() => setLogsExpanded(!logsExpanded)}
            className="flex items-center gap-1.5 text-2xs text-daydra-muted hover:text-daydra-dim transition-colors"
          >
            <ChevronDown
              size={12}
              className={cn("transition-transform", logsExpanded && "rotate-180")}
            />
            Build logs
          </button>
          {logsExpanded && (
            <pre className="mt-2 p-2 rounded-lg bg-black/30 text-2xs text-daydra-muted font-mono overflow-x-auto max-h-40 overflow-y-auto">
              {data.logs}
            </pre>
          )}
        </div>
      )}
    </Card>
  );
}
